import { getGeom } from '@turf/invariant';
import { featureCollection, feature as turfFeature } from '@turf/helpers';
import lineIntersect from '@turf/line-intersect';
import difference from '@turf/difference';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Draw } from 'ol/interaction';
import { GEOMETRY_TYPES, UNSET_PROPERTIES } from 'woodpecker';
import { GEO_JSON, generateUniqueID } from 'macaw';
import { Feature } from 'ol';
import buffer from '@turf/buffer';
import { drawStyle } from '../../../helpers/helpers';
import { canAutoFinishDrawing, drawHandler } from '../../../helpers/interactionUtils';
import { undoRedoPush } from '../../mapLayer/mapInit';
import ToolAbstract from '../../utilityclasses/ToolAbstractClass';
import MapBase from '../../mapLayer/mapBase';

const { LINESTRING } = GEOMETRY_TYPES;

class SplitPolygon extends ToolAbstract {
  private mapObj: MapBase;

  private draw: Draw | null;

  private lineLayer: any;

  private selected_layer_id: string;

  private snap: any;

  constructor(mapObj: MapBase) {
    super();
    this.mapObj = mapObj;
    this.draw = null;
    this.lineLayer = null;
    this.selected_layer_id = '';
  }

  init(id: string) {
    this.off();
    this.selected_layer_id = id;
    const sourceDrawnLines = new VectorSource({ wrapX: false });
    this.lineLayer = new VectorLayer({
      source: sourceDrawnLines
    });
    this.mapObj.map?.addLayer(this.lineLayer);

    this.draw = new Draw({
      source: sourceDrawnLines,
      //@ts-ignore
      type: LINESTRING,
      style: drawStyle(id),
      dragVertexDelay: 0,
      snapTolerance: 1,
      finishCondition: canAutoFinishDrawing,
      condition: (e: any) => drawHandler(e, this.draw as Draw)
    });

    this.mapObj.map?.addInteraction(this.draw);
    this.snap = this.mapObj.getSnap();
    this.snap.forEach((snap: any) => {
      this.mapObj.map?.addInteraction(snap);
    });
    this.draw.on('drawend', this.drawEnd);
    window.addEventListener('keydown', this.handleKeyDawn);
  }

  handleKeyDawn = (e: any) => {
    if (e.code === 'Backspace') {
      this.draw?.removeLastPoint();
    } else if (e.code === 'Space') {
      this.draw?.finishDrawing();
    }
  };

  drawEnd = (e: any) => {
    const drawnFeature = e.feature;
    const drawnExtent = drawnFeature.getGeometry().getExtent();
    const type = drawnFeature.getGeometry().getType();
    setTimeout(() => {
      const layerPoly = this.mapObj.getLayerById(this.selected_layer_id);
      if (layerPoly) {
        const sourcePoly = layerPoly.getSource();
        const featuresInExtent = sourcePoly.getFeaturesInExtent(drawnExtent);
        const drawnGeoJSON = GEO_JSON.writeFeatureObject(drawnFeature);
        const drawnGeometry = getGeom(drawnGeoJSON as any);
        if (type === GEOMETRY_TYPES.LINESTRING) {
          featuresInExtent.forEach((feature: Feature) => {
            const featureGeo = GEO_JSON.writeFeatureObject(feature);
            const polygon = getGeom(featureGeo as any);
            try {
              const cutPolygon = this.polygonCut(polygon, drawnGeometry);

              if (cutPolygon != null) {
                const cutFeatures = GEO_JSON.readFeatures(cutPolygon);
                cutFeatures.forEach((cutFeature: Feature) => {
                  UNSET_PROPERTIES.forEach(property => {
                    cutFeature.unset(property, false);
                  });
                });
                sourcePoly.addFeatures(cutFeatures);
                sourcePoly.removeFeature(feature);
              }
            } catch {
              //   setExtra("feature", JSON.stringify(fcopy));
              //   setExtra("drawn", JSON.stringify(drawnGeometry));
              //   setExtra("Request ID", localStorage.getItem("job_id"));
              //   captureException(err);
            }
          });
        }
        sourcePoly.getFeatures().forEach((feat: Feature, index: number) => {
          feat.setId(generateUniqueID() + index);
        });
        undoRedoPush();
      }
      this.mapObj.map?.removeLayer(this.lineLayer);
    }, 10);
  };

  polygonCut(polygon: any, line: any) {
    const SPLIT_LINE_WIDTH = 0.00001; // Need to reduce because when number of splits performs on single feature that will increases the actual size of the polygon
    const SPLIT_LINE_WIDTH_UNIT = 'meters';

    if ((polygon.type !== 'Polygon' && polygon.type !== 'MultiPolygon') || line.type !== 'LineString') {
      return null;
    }

    const intersectPoints = lineIntersect(polygon, line);
    if (intersectPoints.features.length === 0) {
      return null;
    }

    // linePolygon represents an extremely narrow polygon made using the line
    const linePolygon = buffer(line, SPLIT_LINE_WIDTH, { units: SPLIT_LINE_WIDTH_UNIT });
    // @ts-expect-error TS(2345): Argument of type 'Feature<Polygon, Properties>' is... Remove this comment to see the full error message
    const splittedPolygon = difference(polygon, linePolygon);

    // @ts-expect-error TS(2531): Object is possibly 'null'.
    if (splittedPolygon.geometry.type === 'MultiPolygon') {
      // @ts-expect-error TS(2339): Property 'coordinates' does not exist on type 'Geo... Remove this comment to see the full error message
      const { coordinates } = getGeom(splittedPolygon);
      const cutFeatures = coordinates.map((coord: [number, number][][]) =>
        turfFeature({ type: 'Polygon', coordinates: coord })
      );
      return featureCollection(cutFeatures);
    }

    return splittedPolygon;
  }

  off() {
    this.mapObj.map?.removeInteraction(this.draw as Draw);
    if (this.lineLayer) this.mapObj.map?.removeLayer(this.lineLayer);
    window.removeEventListener('keydown', this.handleKeyDawn);
  }
}

export default SplitPolygon;
